import { aiAllowed } from "@/lib/ai-access";
import { keymap } from "@codemirror/view";
import { createTwoFilesPatch } from "diff";
import { Bot, Copy, ExternalLink, FilePlus2, FileX2, Pencil } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { create } from "zustand";
import { AGENT_LIMITS, claudeModelName, type AgentChange, type AgentResponse } from "@ide/shared";
import { DiffView } from "@/components/diff-view";
import { ClaudeMark } from "@/components/icons";
import { Markdown } from "@/components/markdown";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter } from "@/components/ui/overlay";
import { Badge, Select, Spinner, Textarea } from "@/components/ui/primitives";
import { desktop } from "@/desktop/bridge";
import { useDesktopUi } from "@/desktop/ui";
import {
  breakdownRows,
  formatCount,
  formatTokens,
  formatUsd,
  monthTotal,
  totalTokens,
  useAiUsage,
  usageRows,
  type ProviderUsage,
} from "@/lib/ai-usage";
import { AI_BASE, api, errorMessage } from "@/lib/api";
import { cn, copyToClipboard } from "@/lib/utils";
import { activeEditor } from "@/workspace/active-editor";
import { useAiPrompt, useClaudeAccount } from "@/workspace/ai-panel";
import {
  formatClockTime,
  tokensLabel,
  toneOfUsed,
  toneText,
  UsageAction,
  UsageCard,
  UsageHover,
  UsageLine,
  UsageNotice,
  UsageRows,
  UsageSection,
  UsageStat,
} from "@/workspace/ai-usage";
import { openClaudeConnect } from "./claude-connect";
import { activeWorkspace, deleteFile, readFile, writeFile } from "@/workspace/file-ops";
import { useWorkspace } from "@/workspace/store";
import type { PluginCommand } from "./registry";

// ───────────── Agente Claude ─────────────

/**
 * No app, o agente é o da lateral direita (com a chave da pessoa, ferramentas e permissões);
 * na web, o plano de mudanças pela IA do servidor.
 */
export const useAgent = create<{ open: boolean; setOpen(open: boolean): void }>((set) => ({
  open: false,
  setOpen: (open) => {
    if (open && desktop?.assistant) {
      useDesktopUi.getState().set({ side: "agent" });
      return;
    }
    set({ open });
  },
}));

interface PlannedChange extends AgentChange {
  before: string | null;
  selected: boolean;
}

const ACTION_META = {
  create: { label: "novo", icon: FilePlus2 },
  update: { label: "alterado", icon: Pencil },
  delete: { label: "apagado", icon: FileX2 },
} as const;

function patchOf(c: PlannedChange) {
  return createTwoFilesPatch(c.path, c.path, c.before ?? "", c.content ?? "", "", "", { context: 3 });
}

export function AgentOverlay() {
  const { open, setOpen } = useAgent();
  const [task, setTask] = useState("");
  const [withSelection, setWithSelection] = useState(true);
  const [busy, setBusy] = useState<"thinking" | "applying" | null>(null);
  const [plan, setPlan] = useState<(Omit<AgentResponse, "changes"> & { changes: PlannedChange[] }) | null>(
    null,
  );
  const [expanded, setExpanded] = useState<string | null>(null);
  const ws = activeWorkspace.current;
  if (!open || !ws) return null;
  const canWrite = ws.session.canWrite;
  const aiReady = ws.config.aiAvailable && aiAllowed(ws.detail);

  async function run() {
    const w = activeWorkspace.current;
    if (!w) return;
    setBusy("thinking");
    setPlan(null);
    try {
      const selection = withSelection ? activeEditor.current?.selection().text || undefined : undefined;
      const res = await api<AgentResponse>("/agent", {
        method: "POST",
        base: AI_BASE,
        body: {
          projectId: w.detail.project.id,
          task: task.trim(),
          ...(useWorkspace.getState().active ? { activeFileId: useWorkspace.getState().active! } : {}),
          ...(selection ? { selection } : {}),
        },
      });
      useAiUsage
        .getState()
        .record(
          "kompil",
          w.detail.project.id.toLowerCase(),
          { requests: 1 },
          { name: w.detail.project.name, model: res.model, feature: "Agente" },
        );
      const changes = await Promise.all(
        res.changes.map(async (c) => ({
          ...c,
          before: c.action === "create" ? null : await readFile(w.session, c.path),
          selected: true,
        })),
      );
      setPlan({ ...res, changes });
      setExpanded(changes[0]?.path ?? null);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(null);
    }
  }

  async function apply() {
    const w = activeWorkspace.current;
    if (!w || !plan) return;
    setBusy("applying");
    let done = 0;
    try {
      for (const c of plan.changes.filter((x) => x.selected)) {
        if (c.action === "delete") deleteFile(w.session, c.path);
        else await writeFile(w.session, c.path, c.content ?? "");
        done++;
      }
      toast.success(`${done} arquivo(s) aplicados`, {
        description: "As mudanças entraram no histórico com a sua autoria. Desfaça com Ctrl+Z no arquivo.",
      });
      setPlan(null);
      setTask("");
      setOpen(false);
    } catch (err) {
      toast.error(`Parou depois de ${done} arquivo(s): ${errorMessage(err)}`);
    } finally {
      setBusy(null);
    }
  }

  const toggle = (path: string) =>
    setPlan(
      (p) =>
        p && { ...p, changes: p.changes.map((c) => (c.path === path ? { ...c, selected: !c.selected } : c)) },
    );
  const selectedCount = plan?.changes.filter((c) => c.selected).length ?? 0;

  return (
    <Dialog open onOpenChange={(o) => !o && busy !== "applying" && setOpen(false)}>
      <DialogContent
        wide
        title="Agente Claude"
        description="Descreva a tarefa. O agente lê o projeto e propõe as alterações; nada muda até você aplicar."
      >
        {!aiReady ? (
          <p className="text-[13px] text-fg-muted">
            {ws.config.aiAvailable
              ? "A IA está desligada neste projeto. O dono pode ligá-la nas configurações do projeto."
              : "A IA não está configurada neste servidor."}
          </p>
        ) : (
          <div className="space-y-3">
            <Textarea
              rows={4}
              value={task}
              maxLength={AGENT_LIMITS.maxTaskChars}
              onChange={(e) => setTask(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && (e.ctrlKey || e.metaKey) && task.trim().length >= 5) void run();
              }}
              placeholder={
                "Ex.: crie um endpoint POST /users com validação dos campos e um teste.\nEx.: troque o console.log por um logger com níveis.\nCtrl+Enter envia."
              }
              autoFocus
            />
            <div className="flex flex-wrap items-center justify-between gap-2">
              <label className="flex items-center gap-1.5 text-[12px] text-fg-muted">
                <input
                  type="checkbox"
                  checked={withSelection}
                  onChange={(e) => setWithSelection(e.target.checked)}
                />{" "}
                Incluir o trecho selecionado
              </label>
              <Button
                variant="primary"
                loading={busy === "thinking"}
                disabled={task.trim().length < 5 || busy !== null}
                onClick={() => void run()}
              >
                <Bot className="h-3.5 w-3.5" /> {plan ? "Gerar de novo" : "Planejar alterações"}
              </Button>
            </div>
            {busy === "thinking" ? (
              <div className="flex items-center gap-2 rounded-lg border border-border bg-bg-subtle px-3 py-3 text-[12.5px] text-fg-muted">
                <Spinner /> O agente está lendo o projeto e escrevendo as alterações…
              </div>
            ) : null}
            {plan ? (
              <div className="space-y-3 border-t border-border pt-3">
                <Markdown text={plan.summary} />
                {plan.notes.length ? (
                  <ul className="list-inside list-disc text-[12.5px] text-fg-muted">
                    {plan.notes.map((n, i) => (
                      <li key={i}>{n}</li>
                    ))}
                  </ul>
                ) : null}
                {plan.changes.length === 0 ? (
                  <p className="text-[12.5px] text-fg-subtle">Nenhuma alteração proposta.</p>
                ) : (
                  <ul className="space-y-2">
                    {plan.changes.map((c) => {
                      const meta = ACTION_META[c.action];
                      const Icon = meta.icon;
                      return (
                        <li key={c.path} className="rounded-lg border border-border">
                          <div className="flex items-center gap-2 px-3 py-2">
                            <input
                              type="checkbox"
                              checked={c.selected}
                              onChange={() => toggle(c.path)}
                              aria-label={`Aplicar ${c.path}`}
                            />
                            <Icon className="h-3.5 w-3.5 text-fg-muted" />
                            <button
                              className="min-w-0 flex-1 truncate text-left font-mono text-[12.5px] hover:underline"
                              onClick={() => setExpanded(expanded === c.path ? null : c.path)}
                            >
                              {c.path}
                            </button>
                            <Badge>{meta.label}</Badge>
                            {c.content !== null ? (
                              <Button
                                variant="ghost"
                                size="icon-sm"
                                aria-label="Copiar conteúdo"
                                onClick={() =>
                                  void copyToClipboard(c.content!).then(() => toast.success("Copiado"))
                                }
                              >
                                <Copy className="h-3 w-3" />
                              </Button>
                            ) : null}
                          </div>
                          <p className="px-3 pb-2 text-[12px] text-fg-muted">{c.reason}</p>
                          {expanded === c.path ? (
                            <DiffView patch={patchOf(c)} className={cn("mx-3 mb-3 max-h-80")} />
                          ) : null}
                        </li>
                      );
                    })}
                  </ul>
                )}
                {plan.rejected.length ? (
                  <details className="text-[12px] text-fg-subtle">
                    <summary className="cursor-pointer">
                      {plan.rejected.length} alteração(ões) descartada(s) pela validação
                    </summary>
                    <ul className="mt-1 space-y-0.5">
                      {plan.rejected.map((r, i) => (
                        <li key={i} className="font-mono">
                          {r.path}: {r.reason}
                        </li>
                      ))}
                    </ul>
                  </details>
                ) : null}
              </div>
            ) : null}
          </div>
        )}
        <DialogFooter>
          {plan && plan.changes.length ? (
            <>
              {!canWrite ? (
                <span className="mr-auto self-center text-[11.5px] text-fg-subtle">
                  Seu papel não edita arquivos: copie o conteúdo e proponha como sugestão.
                </span>
              ) : null}
              <Button variant="ghost" onClick={() => setPlan(null)} disabled={busy !== null}>
                Descartar
              </Button>
              <Button
                variant="primary"
                loading={busy === "applying"}
                disabled={!canWrite || selectedCount === 0 || busy !== null}
                onClick={() => void apply()}
              >
                Aplicar {selectedCount} arquivo(s)
              </Button>
            </>
          ) : (
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Fechar
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Orçamentos mensais (US$) que a pessoa pode escolher para a conta da Anthropic. */
const BUDGETS = [5, 10, 20, 50, 100, 200, 500];
const ANTHROPIC_USAGE_URL = "https://console.anthropic.com/settings/usage";

/**
 * Barra de status (canto direito): uso do Claude. Passar o mouse abre o cartão: com a conta da
 * Anthropic, o custo estimado e os tokens do mês (por modelo, recurso e projeto), o orçamento e o
 * limite por minuto que a Anthropic informa a cada resposta; pela IA do Kompil, a cota de hoje; sem
 * conta, o convite para entrar e autorizar.
 */
export function ClaudeStatusItem() {
  const account = useClaudeAccount();
  const own = Boolean(account.data?.connected);
  const months = useAiUsage((s) => s.months);
  const budget = useAiUsage((s) => s.claudeBudgetUsd);
  const total = monthTotal(months, ["claude"]);
  // Sem a conta da Anthropic conectada não há uso para mostrar: só o convite para entrar.
  let text = desktop ? "Claude: entrar" : "Claude";
  let used: number | null = null;
  if (own) {
    text = "Claude";
    if (budget) {
      used = (total.cost / budget) * 100;
      text = used >= 100 ? "Orçamento esgotado" : `${Math.floor(used)}% usado`;
    } else if (total.cost > 0) text = formatUsd(total.cost);
    else if (totalTokens(total)) text = tokensLabel(totalTokens(total));
  }
  return (
    <UsageHover
      label={`Claude: ${text}`}
      className={own ? toneText[toneOfUsed(used)] : undefined}
      trigger={
        <>
          <ClaudeMark className={cn("h-3.5 w-3.5", !own && "opacity-60")} />
          {text}
        </>
      }
    >
      {own ? <ClaudeUsageCard hint={account.data?.hint} /> : <ClaudeSignInCard />}
    </UsageHover>
  );
}

/** Sem conta conectada: só o convite (entrar no console e autorizar o Kompil). */
function ClaudeSignInCard() {
  return (
    <UsageCard
      icon={<ClaudeMark className="h-[18px] w-[18px]" />}
      title="Claude"
      subtitle="Conta da Anthropic não conectada"
    >
      {desktop ? (
        <>
          <p className="text-[12.5px] leading-relaxed text-fg-muted">
            Entre com a sua conta da Anthropic para usar o Claude com os seus limites e ver aqui o custo e os
            tokens de cada modelo, recurso e projeto.
          </p>
          <ol className="space-y-1.5 text-[12px] leading-snug text-fg-muted">
            <li>1. O Kompil abre o console da Anthropic no navegador.</li>
            <li>2. Você entra, cria uma chave e copia.</li>
            <li>3. De volta aqui, autoriza o Kompil a usar o Claude.</li>
          </ol>
          <UsageAction
            variant="primary"
            icon={<ClaudeMark className="h-3.5 w-3.5" mono />}
            onClick={openClaudeConnect}
          >
            Entrar com a Anthropic
          </UsageAction>
        </>
      ) : (
        <p className="text-[12.5px] leading-relaxed text-fg-muted">
          O Claude com a sua conta da Anthropic funciona no Kompil Desktop.
        </p>
      )}
    </UsageCard>
  );
}

function ClaudeUsageCard({ hint }: { hint?: string }) {
  const months = useAiUsage((s) => s.months);
  const breakdown = useAiUsage((s) => s.breakdown);
  const budget = useAiUsage((s) => s.claudeBudgetUsd);
  const limits = useAiUsage((s) => s.claudeLimits);
  const setBudget = useAiUsage((s) => s.setClaudeBudget);
  const setOpen = useAgent((s) => s.setOpen);
  const current = activeWorkspace.current?.detail.project.id.toLowerCase() ?? null;
  const total = monthTotal(months, ["claude"]);
  const models = breakdownRows(breakdown, "claude", "model");
  const features = breakdownRows(breakdown, "claude", "feature");
  const projects = usageRows(months, ["claude"]);
  const tokens = limits?.tokens ?? limits?.input;
  const reset = formatClockTime(tokens?.reset);
  const describe = (u: ProviderUsage) =>
    [
      `${formatCount(u.requests)} ${u.requests === 1 ? "pedido" : "pedidos"}`,
      totalTokens(u) ? tokensLabel(totalTokens(u)) : null,
      u.cost ? formatUsd(u.cost) : null,
    ]
      .filter(Boolean)
      .join(" · ");
  const monthName = new Date().toLocaleDateString("pt-BR", { month: "long" });
  return (
    <UsageCard
      icon={<ClaudeMark className="h-[18px] w-[18px]" />}
      title="Claude · API Anthropic"
      subtitle={`Sua conta Anthropic${hint ? ` · chave ${hint}` : ""}`}
      action={
        <UsageAction variant="outline" onClick={() => desktop?.openExternal(ANTHROPIC_USAGE_URL)}>
          Uso e faturamento
        </UsageAction>
      }
      footer={
        <>
          <UsageAction icon={<Bot className="h-3.5 w-3.5" />} onClick={() => setOpen(true)}>
            Agente Claude
          </UsageAction>
          <span className="ml-auto" />
          <UsageAction
            icon={<ExternalLink className="h-3.5 w-3.5" />}
            onClick={() => desktop?.openExternal(ANTHROPIC_USAGE_URL)}
          >
            Console
          </UsageAction>
        </>
      }
    >
      <>
        <UsageStat
          label={`Gasto em ${monthName}`}
          percentUsed={budget ? (total.cost / budget) * 100 : null}
          value={
            budget ? undefined : (
              <>
                <span className="text-[22px] font-semibold leading-none tracking-tight tabular-nums">
                  {formatUsd(total.cost)}
                </span>
                <span className="text-[12px] text-fg-muted">estimado</span>
              </>
            )
          }
          aside={
            budget ? `${formatUsd(total.cost)} de ${formatUsd(budget)}` : tokensLabel(totalTokens(total))
          }
          hint={`Entrada ${formatTokens(total.input)} · saída ${formatTokens(total.output)} · cache lido ${formatTokens(total.cacheRead)} · cache gravado ${formatTokens(total.cacheWrite)}`}
        />
        <UsageLine label="Orçamento mensal">
          <Select
            variant="ghost"
            align="end"
            value={budget ? String(budget) : ""}
            onChange={(e) => setBudget(e.target.value ? Number(e.target.value) : null)}
            aria-label="Orçamento mensal"
          >
            <option value="">Sem orçamento</option>
            {BUDGETS.map((b) => (
              <option key={b} value={String(b)}>
                {formatUsd(b)} por mês
              </option>
            ))}
          </Select>
        </UsageLine>
        {tokens ? (
          <UsageStat
            label="Limite por minuto"
            percentUsed={100 - (tokens.remaining / tokens.limit) * 100}
            aside={reset ? `enche às ${reset}` : undefined}
            hint={`${formatTokens(tokens.remaining)} de ${formatTokens(tokens.limit)} tokens livres${
              limits?.requests
                ? ` · ${formatCount(limits.requests.remaining)} de ${formatCount(limits.requests.limit)} pedidos`
                : ""
            } (informado pela Anthropic na última resposta)`}
          />
        ) : (
          <UsageNotice>O limite por minuto da sua conta aparece depois da primeira resposta.</UsageNotice>
        )}
      </>
      {models.length ? (
        <UsageSection title="Este mês, por modelo">
          <UsageRows
            rows={models.map((m) => ({ key: m.name, name: claudeModelName(m.name), usage: m.usage }))}
            describe={describe}
          />
        </UsageSection>
      ) : null}
      {features.length ? (
        <UsageSection title="Por recurso">
          {features.map((f) => (
            <UsageLine key={f.name} label={f.name}>
              <span className="tabular-nums text-fg-muted">{describe(f.usage)}</span>
            </UsageLine>
          ))}
        </UsageSection>
      ) : null}
      <UsageSection title="Por projeto">
        <UsageRows
          rows={projects.map((r) => ({ key: r.projectId, name: r.name, usage: r.usage }))}
          current={current}
          describe={describe}
        />
      </UsageSection>
      <p className="text-[11px] leading-snug text-fg-subtle">
        Custo estimado pelos preços públicos da API (Opus 5.5: US$ 4/20 por milhão de tokens de entrada/saída;
        Sonnet 5.5: US$ 2/10; Haiku 5.5: US$ 0,10/0,50). A cobrança oficial está no console da Anthropic.
      </p>
    </UsageCard>
  );
}

export const agentKeymap = () =>
  keymap.of([
    {
      key: "Mod-Shift-a",
      preventDefault: true,
      run: () => {
        useAgent.getState().setOpen(true);
        return true;
      },
    },
  ]);

// ───────────── Copiloto ─────────────

function askAbout(prompt: string, needsSelection = false) {
  const selection = activeEditor.current?.selection().text ?? "";
  if (needsSelection && !selection.trim()) {
    toast("Selecione um trecho de código primeiro.");
    return;
  }
  useAiPrompt.getState().ask(prompt);
}

export const COPILOT_COMMANDS: PluginCommand[] = [
  {
    id: "copilot.explain",
    title: "Copiloto: explicar o código selecionado",
    shortcut: "Ctrl+I",
    run: () =>
      askAbout(
        "Explique, passo a passo, o que o trecho selecionado faz e quais casos de borda ele trata (ou não).",
        true,
      ),
  },
  {
    id: "copilot.tests",
    title: "Copiloto: gerar testes para este arquivo",
    run: () =>
      askAbout(
        "Escreva testes automatizados para o arquivo aberto, usando o framework de testes que o projeto já usa (ou o mais comum da linguagem). Cubra o caminho feliz e os casos de borda.",
      ),
  },
  {
    id: "copilot.document",
    title: "Copiloto: documentar a seleção",
    run: () =>
      askAbout(
        "Escreva a documentação (docstring/JSDoc/Javadoc, conforme a linguagem) para o trecho selecionado: o que faz, parâmetros, retorno e erros.",
        true,
      ),
  },
  {
    id: "copilot.bugs",
    title: "Copiloto: procurar bugs neste arquivo",
    run: () =>
      askAbout(
        "Revise o arquivo aberto procurando bugs, condições de corrida, falhas de segurança e casos de borda não tratados. Liste do mais grave para o menos grave, com a correção sugerida.",
      ),
  },
  {
    id: "copilot.refactor",
    title: "Copiloto: sugerir refatoração da seleção",
    run: () =>
      askAbout(
        "Sugira uma refatoração do trecho selecionado para deixá-lo mais legível e fácil de testar, sem mudar o comportamento. Mostre o código final e explique as mudanças.",
        true,
      ),
  },
];

export const copilotKeymap = () =>
  keymap.of([
    {
      key: "Mod-i",
      preventDefault: true,
      run: () => {
        COPILOT_COMMANDS[0]!.run();
        return true;
      },
    },
  ]);

export const AGENT_COMMANDS: PluginCommand[] = [
  {
    id: "agent.open",
    title: "Agente Claude: nova tarefa",
    shortcut: "Ctrl+Shift+A",
    run: () => useAgent.getState().setOpen(true),
  },
];
